import { useEffect, useMemo } from "react";
import "./CandidatesConfigKanban.scss";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Swal from "sweetalert2";
import { API } from "../../../../helpers/API/amplifyApi";
import { apiPut } from "../../../DocumentationConfiguration/utils/api";
import { useSelector } from "react-redux";
import { Badge, message, Modal, Space, Tooltip, Typography } from "antd";
import { Delete, EditIcon, XIcon } from "../../../../assets/icons";
import { toSentenceCase } from "../../../../utils";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import { QuestionsMark } from "../../../Intervistat/Kandidatet/ShtoKandidat/Image";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { programFields as programFieldsReducer } from "../../../../store/actions";

const ICON_PROPS = { fill: "#fff", width: "16", height: "16" };

const CandidatesConfigKanban = ({
  endpoint,
  setRowData,
  setChanges = () => {},
  onItemClick = () => {},
  data,
  programField = {},
  setDeletedFields = () => {},
  groupBy = "step",
  editColumn = () => {},
}) => {
  const dispatch = useDispatch();
  const { Text } = Typography;
  const { programFields } = useSelector((state) => state.programFields);
  const { isDarkMode } = useSelector((state) => state.usersList);

  //region >updateProgramFields()
  const updateProgramFields = (newFieldOptions) => {
    const newProgramFields = [...(programFields || [])];
    const index = newProgramFields.findIndex(
      ({ fieldId }) => fieldId === programField?.fieldId
    );

    if (index > -1) {
      newProgramFields[index] = {
        ...newProgramFields[index],
        fieldOptions: newFieldOptions,
      };
      dispatch(programFieldsReducer(newProgramFields));
    }
  };

  //region DRAG HANDLER
  const dragEndHandler = async (value) => {
    const { source, destination } = value;
    if (
      source.index === destination.index &&
      source.droppableId === destination.droppableId
    ) {
      return;
    } else {
      const sourceCol = source.droppableId.split("-")[1];
      const destinationCol = destination.droppableId.split("-")[1];

      // selected item
      let draggedRow = data?.find((col) => col?.name === sourceCol)?.["fields"][
        source.index
      ];

      draggedRow[groupBy] = destinationCol;

      //delete item from source
      const newSrcColData = data
        ?.find((col) => col?.name === sourceCol)
        ?.["fields"].filter((_, i) => i !== source.index);

      let srcColToSend = newSrcColData;
      let destColToSend;

      if (sourceCol === destinationCol) {
        //add to source with new index
        srcColToSend = newSrcColData.toSpliced(
          destination.index,
          0,
          draggedRow
        );
      } else {
        //add to destination with new index
        destColToSend = data
          ?.find((col) => col?.name === destinationCol)
          ?.["fields"]?.toSpliced(destination.index, 0, draggedRow);
      }

      let toSend = [];
      data?.forEach((col) => {
        if (col?.name === sourceCol) {
          toSend.push({ ...col, fields: srcColToSend });
        } else if (col?.name === destinationCol) {
          toSend.push({ ...col, fields: destColToSend });
        } else {
          toSend.push({ ...col });
        }
      });
      const swalProps = {
        toast: true,
        timer: 3000,
        showConfirmButton: false,
        position: "bottom-end",
        customClass: {
          container: isDarkMode ? "custom-swal-dark" : "custom-swal",
        },
      };
      const oldRowData = [...data];
      setRowData(toSend);
      try {
        Swal.fire({
          title: "Ju lutem prisni...",
          customClass: {
            popup: isDarkMode ? "custom-swal-dark" : "custom-swal",
          },
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        await apiPut(endpoint, programField?.fieldId, {
          fieldOptions: toSend,
        }).then(() => {
          updateProgramFields(toSend);
          Swal.close();
          Swal.mixin({
            ...swalProps,
          }).fire({
            icon: "success",
            title: `${draggedRow?.label} u zhvendos me sukses`,
          });
        });
      } catch (err) {
        message.error("Error ne perditesimin e konfigurimit te kandidateve!");
        setRowData(oldRowData);
        Swal.mixin({
          ...swalProps,
        }).fire({
          icon: "error",
          title: "Perditesimi nuk u krye!",
        });
        console.error("Error:", err);
      }
    }
  };
  // region ON ITEM DELETE
  const onItemDelete = (e, item) => {
    e.stopPropagation();

    setRowData((prev) =>
      prev.map((col) =>
        col?.name === item?.step
          ? {
              ...col,
              fields: col?.fields?.filter(
                (field) => field?.fieldId !== item?.fieldId
              ),
            }
          : col
      )
    );
    setDeletedFields((prev) =>
      prev.map((col) =>
        col?.name === item?.step
          ? {
              ...col,
              fields: col?.fields?.map((el) =>
                el?.fieldId === item?.fieldId
                  ? { ...el, deletedField: true }
                  : el
              ),
            }
          : col
      )
    );
    setChanges(true);
  };

  //region ON COLUMN DELETE
  const onColumnDelete = async (item) => {
    Modal.confirm({
      title: "A jeni të sigurt?",
      icon: <QuestionsMark />,
      content: (
        <Text>
          Ju po fshini një step të konfigurimit të kandidatëve,{" "}
          <span className="text-bold">së bashku me form items përkatës!</span>
          <Space className="space-gap">
            Nëse jeni të sigurt për këtë veprim, klikoni
            <span className="text-bold">"Po, fshije"</span>
          </Space>
        </Text>
      ),
      okText: "Po, fshije",
      cancelText: "Jo, mos e fshi",
      width: "660px",
      className: `cancelModal ${isDarkMode ? "dark" : ""}`,
      centered: true,
      cancelButtonProps: {
        type: "primary",
        style: {
          background: "#ea3943",
          borderColor: "#ea3943",
          borderRadius: "5px",
        },
      },
      okButtonProps: {
        className: "ant-btn-confirm",
      },
      onOk() {
        const oldFieldOptions = data || [];
        const index = oldFieldOptions.findIndex(
          (el) => el?.name === item?.name
        );
        if (index !== -1) {
          let newFieldOptions = [...oldFieldOptions];
          newFieldOptions.splice(index, 1);

          for (let i = index; i < newFieldOptions.length; i++) {
            const field = newFieldOptions[i];
            newFieldOptions[i] = { ...field, step: field?.step - 1 };
          }
          setRowData(newFieldOptions);
          setChanges(true);
        }
      },
      onCancel: () => false,
    });
  };

  //region RETURN
  return (
    <DragDropContext
      onDragStart={() => {}}
      onDragEnd={(val) => dragEndHandler(val)}
    >
      <div className={`kanbanContainer ${isDarkMode ? "dark" : ""}`}>
        {data?.map((el, idx) => (
          <Droppable droppableId={`droppable-${el?.name}`} key={idx}>
            {(provided, snapshot) => (
              <div
                className="kanbanColumn"
                {...provided?.droppableProps}
                ref={provided?.innerRef}
              >
                <div className="kanbanColHeader">
                  {toSentenceCase(el?.name)}
                  <div className="kanbanEditColBtns">
                    <MondayButton
                      className="mondayButtonRed"
                      onClick={() => onColumnDelete(el)}
                      Icon={<Delete {...ICON_PROPS} />}
                      data-testid="close-btn"
                    />
                    <MondayButton
                      className="mondayButtonGrey"
                      onClick={() =>
                        editColumn({ name: el?.name, step: el?.step })
                      }
                      data-testid="edit-btn"
                      Icon={<EditIcon {...ICON_PROPS} />}
                    />
                  </div>
                </div>
                <div className="kanbanColContent">
                  {el?.fields?.map((item, i) => (
                    <Draggable
                      draggableId={`draggable-${el?.name}-${i}`}
                      index={i}
                      key={i}
                    >
                      {(provided, snapshot) => (
                        <Tooltip
                          title={"Click to edit"}
                          placement="top"
                          mouseEnterDelay={0.5}
                        >
                          <div
                            className="kanbanItem"
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                            onClick={() => onItemClick(item)}
                          >
                            <div
                              className="deleteItem"
                              onClick={(e) => onItemDelete(e, item)}
                            >
                              <Delete width={22} height={22} fill="#ea3943" />
                            </div>
                            <div className="itemLabels">
                              <span className="itemLabel">
                                {item?.label.length > 25
                                  ? item?.label.slice(0, 25) + "..."
                                  : item?.label}
                              </span>
                              <span className="itemFormName">
                                {`(${item?.formItemName})`}
                              </span>
                            </div>
                            {!!item?.required ? (
                              <Badge.Ribbon text={"required"} color="red" />
                            ) : null}
                          </div>
                        </Tooltip>
                      )}
                    </Draggable>
                  ))}
                </div>
              </div>
            )}
          </Droppable>
        ))}
      </div>
    </DragDropContext>
  );
};

export default CandidatesConfigKanban;

CandidatesConfigKanban.propTypes = {
  endpoint: PropTypes.string.isRequired,
  setRowData: PropTypes.func.isRequired,
  setChanges: PropTypes.func,
  onItemClick: PropTypes.func,
  data: PropTypes.array.isRequired,
  programField: PropTypes.object,
  setDeletedFields: PropTypes.func,
  groupBy: PropTypes.string,
  editColumn: PropTypes.object,
};
